// Phase-3 Slice 27 — group ReferenceRecord[] by containing object.
// Extracted from `renamePlanner.ts` so the planner stays under the 200-line
// budget; also reusable by Slice 28's executor (per-object commit batching)
// and by future find-references-shaped consumers if they need stable groups
// without going through `findReferencesService`.

import { FOLD_BOTH, objectKeyFor, type ObjectKeyCase } from '../index/identifierIndex';
import type { ObjectKind, QualifiedName, ReferenceRecord } from '../types';

export type RefGroup = {
  readonly objectKind: ReferenceRecord['objectKind'];
  readonly objectName: QualifiedName;
  readonly references: readonly ReferenceRecord[];
};

/** How an object of `kind` is keyed. Production passes the INDEX's own
 *  `objectKeyCaseFor` — the policy it actually froze — rather than
 *  `navCasePolicy.objectKeyCaseFor`, which answers for a
 *  `(connection, engine, database)` triple the grouping caller does not
 *  reliably hold. */
export type ObjectKeyCasePolicy = (kind: ObjectKind) => ObjectKeyCase | undefined;

/**
 * Group `records` by the object whose body holds them — under the SAME key
 * policy the identifier index used. A group is one body to rewrite, so folding
 * here what the index keeps apart (MySQL `DbA.P` next to `dba.P`, a SQL Server
 * `_CS_` database's `dbo.V` next to `dbo.v`) plans both objects' ranges against
 * one body. `policy` omitted, or answering `undefined`, folds — the
 * over-inclusive default, and what a buffer-local index does.
 */
export function groupByObject(
  records: readonly ReferenceRecord[],
  policy?: ObjectKeyCasePolicy,
): readonly RefGroup[] {
  const buckets = new Map<string, ReferenceRecord[]>();
  const headers = new Map<
    string,
    { objectKind: ReferenceRecord['objectKind']; objectName: QualifiedName }
  >();
  for (const ref of records) {
    const key = objectKeyFor(ref.objectKind, ref.objectName, policy?.(ref.objectKind) ?? FOLD_BOTH);
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = [];
      buckets.set(key, bucket);
      headers.set(key, { objectKind: ref.objectKind, objectName: ref.objectName });
    }
    bucket.push(ref);
  }
  const groups: RefGroup[] = [];
  for (const [key, refs] of buckets) {
    const header = headers.get(key);
    if (!header) continue;
    groups.push({ objectKind: header.objectKind, objectName: header.objectName, references: refs });
  }
  return groups;
}
